import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Users, Mail, Phone, Briefcase, Ban, CheckCircle2, Package } from "lucide-react";
import { SkeletonList } from "@/components/admin/Skeleton";

export default function ClientsPanel({ API, getAuthHeaders }) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  const fetchClients = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/clients`, { headers: getAuthHeaders() });
      setClients(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load clients");
    }
    setLoading(false);
  }, [API, getAuthHeaders]);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  const toggleActive = async (id) => {
    try {
      const { data } = await axios.patch(`${API}/admin/clients/${id}/toggle-active`, {}, { headers: getAuthHeaders() });
      setClients(prev => prev.map(c => c.id === id ? { ...c, is_active: data.is_active } : c));
      toast.success(data.is_active ? "Client enabled" : "Client disabled");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Toggle failed");
    }
  };

  const q = query.trim().toLowerCase();
  const filtered = !q ? clients : clients.filter(c =>
    (c.name || "").toLowerCase().includes(q) ||
    (c.email || "").toLowerCase().includes(q) ||
    (c.business_name || "").toLowerCase().includes(q) ||
    (c.phone || "").includes(q)
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-lg font-black uppercase tracking-tight">Registered Clients</h2>
          <p className="text-xs text-zinc-500">Everyone who signed up on the client portal.</p>
        </div>
        <input
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Search name, email, phone..."
          data-testid="clients-search"
          className="w-full sm:w-72 bg-transparent border border-white/10 px-4 py-2 text-sm text-white focus:border-red-600 outline-none"
        />
      </div>

      {loading ? <SkeletonList count={4} /> :
        filtered.length === 0 ? (
          <div className="border border-dashed border-white/10 bg-[#111] p-12 text-center">
            <Users size={32} className="text-zinc-700 mx-auto mb-3" />
            <p className="text-white font-bold uppercase tracking-wider text-sm">{clients.length === 0 ? "No clients yet" : "No match for your search"}</p>
            <p className="text-xs text-zinc-500 mt-1">Clients appear here after they sign up on /login.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {filtered.map(c => (
              <div key={c.id} data-testid={`client-row-${c.id}`} className={`border p-4 transition-colors ${c.is_active ? "border-white/5 bg-[#111]" : "border-red-600/20 bg-red-600/5"}`}>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 bg-red-600 flex items-center justify-center font-black text-white text-sm shrink-0 overflow-hidden">
                      {c.avatar_url ? <img src={`${process.env.REACT_APP_BACKEND_URL}${c.avatar_url}`} alt="" className="w-full h-full object-cover" /> : (c.name?.[0]?.toUpperCase() || "?")}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-white truncate">{c.name}</p>
                      {c.business_name && <p className="text-[11px] text-zinc-500 flex items-center gap-1 truncate"><Briefcase size={10} /> {c.business_name}</p>}
                    </div>
                  </div>
                  <button
                    onClick={() => toggleActive(c.id)}
                    data-testid={`client-toggle-${c.id}`}
                    title={c.is_active ? "Disable account" : "Enable account"}
                    className={`tap inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest border px-2 py-1 transition-colors ${c.is_active ? "border-green-600/30 text-green-400 hover:bg-green-600/10" : "border-red-600/40 text-red-400 hover:bg-red-600/10"}`}
                  >
                    {c.is_active ? <><CheckCircle2 size={11} /> Active</> : <><Ban size={11} /> Disabled</>}
                  </button>
                </div>
                <div className="space-y-1 text-xs">
                  <a href={`mailto:${c.email}`} className="tap flex items-center gap-1.5 text-zinc-400 hover:text-red-500 truncate"><Mail size={11} /> {c.email}</a>
                  {c.phone && (
                    <a href={`https://wa.me/${c.phone.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="tap flex items-center gap-1.5 text-zinc-400 hover:text-green-500"><Phone size={11} /> {c.phone}</a>
                  )}
                </div>
                <div className="mt-3 pt-3 border-t border-white/5 flex items-center justify-between text-[10px] uppercase tracking-widest text-zinc-500">
                  <span className="flex items-center gap-1"><Package size={10} /> {c.order_count} order{c.order_count === 1 ? "" : "s"}</span>
                  <span>Joined {new Date(c.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}</span>
                </div>
              </div>
            ))}
          </div>
        )
      }
    </div>
  );
}
